// src/components/messaging/MessagingIconSimple.tsx
// Simplified version that just calls context functions
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useMessaging } from '../../contexts/MessagingContext.tsx';

interface MessagingIconSimpleProps {
  targetUser?: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
  };
  className?: string;
}

export const MessagingIconSimple: React.FC<MessagingIconSimpleProps> = ({ 
  targetUser, 
  className = "" 
}) => {
  const { user } = useAuth();
  const { openMessagingModal, openChatWithUser } = useMessaging();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user && !targetUser) {
      // Only fetch unread count for own profile
      fetchUnreadCount();
    }
  }, [user, targetUser]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://aztrainersbackend.onrender.com/api/conversations/', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const totalUnread = data.conversations.reduce((sum: number, conv: any) => sum + conv.unread_count, 0);
        setUnreadCount(totalUnread);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleClick = () => {
    if (targetUser) {
      // For other user's profile - start or continue conversation
      openChatWithUser(targetUser);
    } else {
      // For own profile - show messaging modal
      openMessagingModal();
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`relative inline-flex items-center justify-center p-2 rounded-full hover:bg-white/20 transition-colors ${className}`}
      title={targetUser ? `Message ${targetUser.username}` : 'Messages'}
    >
      <svg 
        className="w-6 h-6 text-gray-700" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
        />
      </svg>
      
      {/* Unread count badge (only for own profile) */}
      {!targetUser && unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};